/**
 * workflowReducer.ts — WorkflowSession 容器树 + agent loop 双轨归约
 *
 * 职责：
 *     结构事件经 WorkflowContainerTreeProjector 更新 containerTree；
 *     agent 事件 bind loopSessionId 并写入 loopTreesBySessionId。
 *
 * 链路位置：
 *     workflowSessionStore.applyEvent → reduceWorkflowSession。
 *
 * 当前裁剪范围：
 *     workflowProgress 为过渡期 dual-write；P2+ UI 改读 containerTree。
 */
import type { LangchainAgentEvent } from '../types/events';
import type { SessionTree } from '../types/tree';
import {
  createWorkflowRunningLoopTree,
  finalizeWorkflowLoopTree,
} from './workflow/workflowLoopTreeUtils';
import { containersByLoopSessionId } from './workflow/workflowLoopEviction';
import type {
  WorkflowContainerRuntimeNode,
  WorkflowContainerStatus,
  WorkflowContainerTreeState,
} from '../types/workflowContainer';
import { containerTreeToSnapshot } from '../types/workflowContainer';
import type {
  WorkflowSessionState,
  WorkflowStageProgress,
  ParallelItemProgress,
  WorkflowRunStatus,
  WorkflowProgressState,
} from '../types/workflowSession';
import { createEmptyWorkflowSessionState } from '../types/workflowSession';
import {
  buildContainerId,
  mergeRuntimeContainerNodes,
  reduceContainerSnapshot,
  resolveContentScopeKey,
  resolveContentTargetFromSnapshot,
  resolveScopeKey,
} from './workflow/workflowContainerProjector';
import {
  ensureEmbeddedSubworkflowContainers,
  findContainerIdByLoopSession,
  patchEmbeddedStructureEvent,
  resolveStructureOpenLoopSessionId,
  settleCompletedStageSubtree,
  shouldPrebindLoopSessionFromStructureOpen,
  shouldUseEmbeddedSynthesis,
} from './workflow/embeddedSubworkflowUtils';
import {
  deriveLoopSessionIdFromAgentEvent,
  deriveLoopSessionIdFromStructureOpen,
  loopTaskScopeForStructureOpen,
} from './workflow/loopSessionUtils';
import {
  clearHostStageLoopOnNestedChildOpen,
  isNestedHostStageShell,
  dropStaleEmbeddedPathsOnNativeSubworkflowStart,
} from './workflow/nativeNestedUtils';
import { isWorkflowStructureEventType } from './workflowStructureEvents';
import { reduceTree, type ReduceOptions } from './reducer';

function readString(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

function mapContainerStatusToProgressStatus(
  status: WorkflowContainerStatus,
): WorkflowStageProgress['status'] {
  switch (status) {
    case 'completed':
      return 'done';
    case 'failed':
      return 'failed';
    case 'running':
      return 'running';
    default:
      return 'pending';
  }
}

function syncWorkflowProgressFromContainerTree(
  tree: WorkflowContainerTreeState,
  prev: WorkflowProgressState,
): WorkflowProgressState {
  const workflowNodes = Object.values(tree.containersById).filter((n) => n.scope === 'workflow');
  const rootWorkflow =
    workflowNodes.find((n) => tree.rootContainerIds.includes(n.container_id)) ?? workflowNodes[0];

  const stages: WorkflowStageProgress[] = Object.values(tree.containersById)
    .filter((n) => n.scope === 'stage')
    .sort((a, b) => {
      const ai = a.display?.progress_current ?? 0;
      const bi = b.display?.progress_current ?? 0;
      if (ai !== bi) return ai - bi;
      return a.scope_key.localeCompare(b.scope_key);
    })
    .map((node, index) => ({
      stageKey: node.scope_key,
      stageIndex: node.display?.progress_current ?? index,
      stageTotal: node.display?.progress_total,
      status: mapContainerStatusToProgressStatus(node.status),
      error: node.display?.status_reason,
    }));

  const parallelItems: Record<string, ParallelItemProgress> = {};
  for (const node of Object.values(tree.containersById)) {
    if (node.scope !== 'item') continue;
    parallelItems[node.scope_key] = {
      itemKey: node.scope_key,
      itemIndex: node.display?.item_index ?? 0,
      itemTotal: node.display?.item_total,
      status: mapContainerStatusToProgressStatus(node.status),
      loopSessionId: node.loopSessionId,
      error: node.display?.status_reason,
    };
  }

  let runStatus: WorkflowRunStatus = prev.status;
  if (rootWorkflow) {
    if (rootWorkflow.status === 'completed') runStatus = 'done';
    else if (rootWorkflow.status === 'failed') runStatus = 'error';
    else runStatus = 'running';
  }

  return {
    workflowId: rootWorkflow?.scope_key ?? prev.workflowId,
    workflowPath: rootWorkflow?.workflow_path ?? prev.workflowPath,
    status: runStatus,
    stages,
    parallelItems,
  };
}

function loopBoundContainerIds(
  containersById: Record<string, WorkflowContainerRuntimeNode>,
): Set<string> {
  const ids = new Set<string>();
  for (const [containerId, node] of Object.entries(containersById)) {
    if (node.loopSessionId) ids.add(containerId);
  }
  return ids;
}

function applyStructureToContainerTree(
  tree: WorkflowContainerTreeState,
  event: LangchainAgentEvent,
): WorkflowContainerTreeState {
  const snapshot = reduceContainerSnapshot(containerTreeToSnapshot(tree), event, {
    suppressContentContainerIds: loopBoundContainerIds(tree.containersById),
  });
  let containersById = mergeRuntimeContainerNodes(tree.containersById, snapshot);
  containersById = enrichWorkflowRootFromStages(
    containersById,
    snapshot.root_container_ids,
  );
  return {
    rootContainerIds: snapshot.root_container_ids,
    activeContainerIds: snapshot.active_container_ids,
    containersById,
  };
}

/** stage-start 等 open 事件：预绑定 loopSessionId + 清空 content_blocks（D24 过程层 SSOT）。 */
function prebindLoopSessionFromStructureOpen(
  tree: WorkflowContainerTreeState,
  event: LangchainAgentEvent,
): WorkflowContainerTreeState {
  const derivedLoopSessionId = deriveLoopSessionIdFromStructureOpen(event);
  const scope = loopTaskScopeForStructureOpen(event.event_type);
  if (!derivedLoopSessionId || !scope) return tree;

  const data = (event.data ?? {}) as Record<string, unknown>;
  let loopSessionId = resolveStructureOpenLoopSessionId(
    event.event_type,
    event.session_id,
    derivedLoopSessionId,
    data,
  );

  // shouldPrebindLoopSessionFromStructureOpen 返回 false 表示 eventSessionId 是"泄漏"的；
  // 此时不应阻止绑定，而是强制使用 derivedLoopSessionId。
  if (
    !shouldPrebindLoopSessionFromStructureOpen(
      event.event_type,
      event.session_id,
      derivedLoopSessionId,
      data,
    )
  ) {
    loopSessionId = derivedLoopSessionId;
  }

  let nextTree =
    event.event_type === 'parallel-aggregate-start'
      ? ensureEmbeddedSubworkflowContainers(tree, loopSessionId)
      : tree;

  const workflowPath = readString(data, 'workflow_path') ?? '';
  const scopeKey = resolveScopeKey(event.event_type, data);
  const containerId = buildContainerId(workflowPath, scope, scopeKey);

  let targetId = containerId;
  const embeddedTargetId = findContainerIdByLoopSession(nextTree, loopSessionId);
  if (embeddedTargetId) {
    targetId = embeddedTargetId;
  }

  const node = nextTree.containersById[targetId];
  if (!node) return nextTree;
  if (node.loopSessionId === loopSessionId && node.content_blocks.length === 0) {
    return nextTree;
  }

  return {
    ...nextTree,
    containersById: {
      ...nextTree.containersById,
      [targetId]: {
        ...node,
        loopSessionId,
        content_blocks: [],
      },
    },
  };
}

/** workflow display 缺 pattern/progress 时从 stage 子节点推导（对齐 D24 根行 meta）。 */
function enrichWorkflowRootFromStages(
  containersById: Record<string, WorkflowContainerRuntimeNode>,
  rootContainerIds: string[],
): Record<string, WorkflowContainerRuntimeNode> {
  const rootId = rootContainerIds[0];
  if (!rootId) return containersById;

  const root = containersById[rootId];
  if (!root || root.scope !== 'workflow') return containersById;

  const stages = Object.values(containersById).filter(
    (node) =>
      node.scope === 'stage'
      && node.workflow_path === root.workflow_path
      && node.workflow_depth === root.workflow_depth,
  );
  if (stages.length === 0) return containersById;

  const pattern =
    root.pattern
    || root.display?.pattern
    || stages.find((s) => s.pattern)?.pattern
    || stages.find((s) => s.display?.pattern)?.display?.pattern
    || 'chaining';

  const inferredTotal = stages.reduce(
    (max, s) => Math.max(max, s.display?.progress_total ?? 0),
    0,
  );
  const progressTotal =
    root.display?.progress_total ?? (inferredTotal > 0 ? inferredTotal : stages.length);

  const settled = stages.filter(
    (s) => s.status === 'completed' || s.status === 'failed',
  ).length;
  const progressCurrent =
    root.status === 'completed' || root.status === 'failed'
      ? progressTotal
      : Math.max(root.display?.progress_current ?? 0, settled);

  if (
    root.pattern === pattern
    && root.display?.pattern === pattern
    && root.display?.progress_total === progressTotal
    && root.display?.progress_current === progressCurrent
  ) {
    return containersById;
  }

  const display = root.display
    ? {
        ...root.display,
        pattern,
        progress_total: progressTotal,
        progress_current: progressCurrent,
      }
    : root.display;

  return {
    ...containersById,
    [rootId]: {
      ...root,
      pattern,
      display,
    },
  };
}

function findContainerByLoopSession(
  tree: WorkflowContainerTreeState,
  loopSessionId: string,
): string | null {
  return findContainerIdByLoopSession(tree, loopSessionId);
}

const WORKFLOW_DEFAULT_LOOP_SESSION_ID = 'workflow-loop-default';

/** Agent loop 事件 session_id；缺省时从 workflow_id + task_key 推导，再回退 active loop，最后才 default。 */
function resolveAgentLoopSessionId(
  event: LangchainAgentEvent,
  activeLoopSessionId: string | null | undefined,
  containerTree: WorkflowContainerTreeState,
): string | null {
  if (event.session_id) return event.session_id;
  if (isWorkflowStructureEventType(event.event_type)) return null;
  if (event.event_type === 'task-list-snapshot') return null;

  const derived = deriveLoopSessionIdFromAgentEvent(event);
  if (derived) return derived;

  const byLoop = containersByLoopSessionId(containerTree);

  if (activeLoopSessionId) {
    const activeContainer = byLoop.get(activeLoopSessionId);
    if (
      activeContainer
      && (activeContainer.status === 'running' || activeContainer.status === 'retrying')
    ) {
      return activeLoopSessionId;
    }
  }

  const runningLoopIds = [...byLoop.entries()]
    .filter(([, node]) => node.status === 'running' || node.status === 'retrying')
    .map(([loopSessionId]) => loopSessionId);
  if (runningLoopIds.length === 1) return runningLoopIds[0]!;

  if (activeLoopSessionId) return activeLoopSessionId;

  return WORKFLOW_DEFAULT_LOOP_SESSION_ID;
}

function bindLoopSessionToContainerTree(
  tree: WorkflowContainerTreeState,
  event: LangchainAgentEvent,
  activeLoopSessionId?: string | null,
): WorkflowContainerTreeState {
  const sessionId = resolveAgentLoopSessionId(event, activeLoopSessionId, tree);
  if (!sessionId) return tree;

  let nextTree = ensureEmbeddedSubworkflowContainers(tree, sessionId);
  if (findContainerByLoopSession(nextTree, sessionId)) return nextTree;

  const data = (event.data ?? {}) as Record<string, unknown>;
  const workflowPath = readString(data, 'workflow_path') ?? null;
  const scopeKey = resolveContentScopeKey(data);

  let targetId: string | null = findContainerByLoopSession(nextTree, sessionId);

  if (!targetId && scopeKey) {
    targetId = resolveContentTargetFromSnapshot(
      containerTreeToSnapshot(nextTree),
      workflowPath,
      scopeKey,
    );
  }

  if (!targetId) {
    const fallbackScopes: Array<'item' | 'aggregate' | 'stage'> = ['item', 'aggregate', 'stage'];
    for (const scope of fallbackScopes) {
      for (const containerId of nextTree.activeContainerIds) {
        const node = nextTree.containersById[containerId];
        if (!node || node.loopSessionId) continue;
        if (node.scope !== scope) continue;
        if (node.scope === 'stage' && isNestedHostStageShell(nextTree, node)) continue;
        targetId = containerId;
        break;
      }
      if (targetId) break;
    }
  }

  if (!targetId) {
    targetId = resolveContentTargetFromSnapshot(
      containerTreeToSnapshot(nextTree),
      workflowPath,
      null,
    );
  }

  if (!targetId) return nextTree;

  const node = nextTree.containersById[targetId];
  if (!node || node.loopSessionId) return nextTree;

  // D24 §8.2: stage / item / branch / aggregate(Agent loop) 承载 loop；workflow / route-decision 等不绑定。
  if (node.scope === 'stage' && isNestedHostStageShell(nextTree, node)) {
    return nextTree;
  }

  if (
    node.scope !== 'stage'
    && node.scope !== 'item'
    && node.scope !== 'branch'
    && node.scope !== 'aggregate'
  ) {
    return nextTree;
  }

  return {
    ...nextTree,
    containersById: {
      ...nextTree.containersById,
      [targetId]: { ...node, loopSessionId: sessionId },
    },
  };
}

const LOOP_TASK_STRUCTURE_CLOSE_EVENTS = new Set([
  'stage-done',
  'stage-failed',
  'parallel-item-done',
  'parallel-item-failed',
  'parallel-aggregate-done',
  'parallel-aggregate-failed',
  'route-branch-done',
  'route-branch-failed',
]);

function resolveStructureCloseLoopSessionId(
  event: LangchainAgentEvent,
): string | null {
  const data = (event.data ?? {}) as Record<string, unknown>;
  const derived =
    deriveLoopSessionIdFromStructureOpen(event)
    ?? deriveLoopSessionIdFromAgentEvent(event);
  if (!derived && !event.session_id) return null;
  if (!derived) return event.session_id ?? null;
  return resolveStructureOpenLoopSessionId(
    event.event_type,
    event.session_id,
    derived,
    data,
  );
}

function finalizeLoopTreesForCompletedContainers(
  state: WorkflowSessionState,
  containerTree: WorkflowContainerTreeState,
): WorkflowSessionState {
  let loopTreesBySessionId = state.loopTreesBySessionId;
  for (const node of Object.values(containerTree.containersById)) {
    if (!node.loopSessionId) continue;
    if (node.status !== 'completed' && node.status !== 'failed') continue;
    const loopTree = loopTreesBySessionId[node.loopSessionId];
    if (!loopTree || loopTree.status === 'done' || loopTree.status === 'error') continue;
    loopTreesBySessionId = {
      ...loopTreesBySessionId,
      [node.loopSessionId]: finalizeWorkflowLoopTree(
        loopTree,
        node.status === 'failed' ? 'error' : 'done',
      ),
    };
  }
  return { ...state, loopTreesBySessionId };
}

function clearActiveLoopIfFinalized(
  state: WorkflowSessionState,
): WorkflowSessionState {
  const activeId = state.activeLoopSessionId;
  if (!activeId) return state;
  const loop = state.loopTreesBySessionId[activeId];
  if (loop && (loop.status === 'done' || loop.status === 'error')) {
    return { ...state, activeLoopSessionId: null };
  }
  return state;
}

function resolveRootWorkflowId(
  containerTree: WorkflowContainerTreeState,
): string | null {
  const rootId = containerTree.rootContainerIds[0];
  const root = rootId ? containerTree.containersById[rootId] : undefined;
  return root?.scope === 'workflow' ? root.scope_key : null;
}
function findContainerForLoopSession(
  tree: WorkflowContainerTreeState,
  loopSessionId: string,
): WorkflowContainerRuntimeNode | undefined {
  return Object.values(tree.containersById).find(
    (node) => node.loopSessionId === loopSessionId,
  );
}

/** 容器已 completed/failed 时，禁止 agent 事件把 scoped loop 拉回 running（SDK dual emission / gate start）。 */
function reconcileLoopTreeWithContainerStatus(
  containerTree: WorkflowContainerTreeState,
  loopSessionId: string,
  loopTree: SessionTree,
): SessionTree {
  const container = findContainerForLoopSession(containerTree, loopSessionId);
  if (!container) return loopTree;
  if (container.status === 'completed') {
    return finalizeWorkflowLoopTree(loopTree, 'done');
  }
  if (container.status === 'failed') {
    return finalizeWorkflowLoopTree(loopTree, 'error');
  }
  return loopTree;
}

function finalizeLoopTreeOnStructureClose(
  state: WorkflowSessionState,
  event: LangchainAgentEvent,
): WorkflowSessionState {
  if (!LOOP_TASK_STRUCTURE_CLOSE_EVENTS.has(event.event_type)) {
    return state;
  }

  const loopSessionId = resolveStructureCloseLoopSessionId(event);
  if (!loopSessionId) return state;

  const loopTree = state.loopTreesBySessionId[loopSessionId];
  if (!loopTree) return state;

  const terminalStatus = event.event_type.endsWith('-failed') ? 'error' : 'done';
  return {
    ...state,
    loopTreesBySessionId: {
      ...state.loopTreesBySessionId,
      [loopSessionId]: finalizeWorkflowLoopTree(loopTree, terminalStatus),
    },
  };
}

function applyStructureEvent(
  state: WorkflowSessionState,
  event: LangchainAgentEvent,
): WorkflowSessionState {
  const rootWorkflowId = resolveRootWorkflowId(state.containerTree);
  const useEmbeddedSynthesis = shouldUseEmbeddedSynthesis(state, event);
  const projectedEvent =
    rootWorkflowId != null && useEmbeddedSynthesis
      ? patchEmbeddedStructureEvent(event, rootWorkflowId)
      : event;

  let containerTree = applyStructureToContainerTree(state.containerTree, projectedEvent);

  if (
    projectedEvent.event_type === 'subworkflow-start'
    || projectedEvent.event_type === 'parallel-item-start'
  ) {
    containerTree = clearHostStageLoopOnNestedChildOpen(containerTree, projectedEvent);
  }
  if (projectedEvent.event_type === 'subworkflow-start') {
    containerTree = dropStaleEmbeddedPathsOnNativeSubworkflowStart(containerTree, projectedEvent);
  }

  const derivedLoopSessionId = deriveLoopSessionIdFromStructureOpen(projectedEvent);
  const structureData = (projectedEvent.data ?? {}) as Record<string, unknown>;
  const structureLoopSessionId = derivedLoopSessionId
    ? resolveStructureOpenLoopSessionId(
        projectedEvent.event_type,
        projectedEvent.session_id,
        derivedLoopSessionId,
        structureData,
      )
    : null;

  if (structureLoopSessionId && useEmbeddedSynthesis) {
    containerTree = ensureEmbeddedSubworkflowContainers(containerTree, structureLoopSessionId);
  }

  containerTree = prebindLoopSessionFromStructureOpen(containerTree, projectedEvent);

  if (
    projectedEvent.event_type === 'stage-done'
    || projectedEvent.event_type === 'stage-failed'
  ) {
    const stageKey = readString(structureData, 'stage_key');
    const workflowPath = readString(structureData, 'workflow_path') ?? '';
    if (stageKey) {
      const stageId = buildContainerId(workflowPath, 'stage', stageKey);
      if (containerTree.containersById[stageId]) {
        containerTree = settleCompletedStageSubtree(containerTree, stageId);
      }
    }
  }

  const loopSessionId = structureLoopSessionId ?? derivedLoopSessionId;
  let nextState: WorkflowSessionState = { ...state, containerTree };

  if (loopSessionId) {
    nextState = ensureLoopTree(nextState, loopSessionId);
  }

  nextState = finalizeLoopTreeOnStructureClose(nextState, projectedEvent);
  nextState = finalizeLoopTreesForCompletedContainers(nextState, containerTree);
  nextState = clearActiveLoopIfFinalized(nextState);

  const workflowProgress = syncWorkflowProgressFromContainerTree(
    containerTree,
    state.workflowProgress,
  );

  let status = nextState.status;
  if (workflowProgress.status === 'done') status = 'done';
  else if (workflowProgress.status === 'error') status = 'error';
  else if (status === 'connecting') status = 'running';

  const base = {
    ...nextState,
    status,
    containerTree,
    workflowProgress,
  };

  if (event.session_id && !state.meta.workflowRunId) {
    return {
      ...base,
      meta: { ...state.meta, workflowRunId: event.session_id },
    };
  }

  return base;
}

function ensureLoopTree(
  state: WorkflowSessionState,
  loopSessionId: string,
): WorkflowSessionState {
  if (state.loopTreesBySessionId[loopSessionId]) return state;
  const loopTree = createWorkflowRunningLoopTree();
  return {
    ...state,
    loopTreesBySessionId: {
      ...state.loopTreesBySessionId,
      [loopSessionId]: loopTree,
    },
    activeLoopSessionId: state.activeLoopSessionId ?? loopSessionId,
  };
}

/** Pure reducer：Workflow 会话状态 + 单条事件。 */
export function reduceWorkflowSession(
  state: WorkflowSessionState,
  event: LangchainAgentEvent,
  eventIndex: number,
  options?: ReduceOptions,
): WorkflowSessionState {
  if (event.event_type === 'task-list-snapshot') {
    const data = event.data as Record<string, unknown>;
    const taskListId =
      typeof data.task_list_id === 'string' ? data.task_list_id : undefined;
    const loopSessionId =
      taskListId ?? event.session_id ?? 'workflow-loop-default';
    let next = ensureLoopTree(state, loopSessionId);
    const loopTree = next.loopTreesBySessionId[loopSessionId]!;
    let reducedLoop = reduceTree(loopTree, event, eventIndex, options);
    reducedLoop = reconcileLoopTreeWithContainerStatus(
      next.containerTree,
      loopSessionId,
      reducedLoop,
    );
    return {
      ...next,
      loopTreesBySessionId: {
        ...next.loopTreesBySessionId,
        [loopSessionId]: reducedLoop,
      },
      activeLoopSessionId: loopSessionId,
    };
  }

  if (isWorkflowStructureEventType(event.event_type)) {
    return applyStructureEvent(state, event);
  }

  const loopSessionId =
    resolveAgentLoopSessionId(event, state.activeLoopSessionId, state.containerTree)
    ?? WORKFLOW_DEFAULT_LOOP_SESSION_ID;
  let containerTree = bindLoopSessionToContainerTree(
    state.containerTree,
    event,
    state.activeLoopSessionId,
  );
  containerTree = applyStructureToContainerTree(containerTree, event);
  let next = ensureLoopTree({ ...state, containerTree }, loopSessionId);
  const loopTree = next.loopTreesBySessionId[loopSessionId]!;
  let reducedLoop = reduceTree(loopTree, event, eventIndex, options);
  reducedLoop = reconcileLoopTreeWithContainerStatus(
    next.containerTree,
    loopSessionId,
    reducedLoop,
  );

  const workflowProgress = syncWorkflowProgressFromContainerTree(
    next.containerTree,
    next.workflowProgress,
  );

  let status = next.status;
  if (workflowProgress.status === 'done') status = 'done';
  else if (workflowProgress.status === 'error') status = 'error';
  else if (reducedLoop.status === 'error') status = 'error';
  else if (status === 'connecting') status = 'running';

  return {
    ...next,
    workflowProgress,
    loopTreesBySessionId: {
      ...next.loopTreesBySessionId,
      [loopSessionId]: reducedLoop,
    },
    activeLoopSessionId: loopSessionId,
    status,
  };
}

/**
 * SSE 正常结束后兜底：根 workflow 已 completed，或全部 top-level stage 已 settle
 * 且无 running 容器时，提升 session 为 done（嵌套 parallel 缺 workflow-end 时不锁死宿主 UI）。
 */
export function finalizeWorkflowSessionOnStreamComplete(
  state: WorkflowSessionState,
): WorkflowSessionState {
  if (state.status === 'done' || state.status === 'error') {
    return { ...state, activeLoopSessionId: null };
  }

  const tree = state.containerTree;
  const rootId = tree.rootContainerIds[0];
  if (!rootId) {
    return { ...state, status: 'done', activeLoopSessionId: null };
  }

  const root = tree.containersById[rootId];
  if (!root) {
    return { ...state, status: 'done', activeLoopSessionId: null };
  }

  const topLevelStages = Object.values(tree.containersById).filter(
    (node) =>
      node.scope === 'stage'
      && node.workflow_path === root.workflow_path
      && node.workflow_depth === root.workflow_depth,
  );

  const progressTotal =
    root.display?.progress_total
    ?? (topLevelStages.reduce(
      (max, s) => Math.max(max, s.display?.progress_total ?? 0),
      0,
    ) || topLevelStages.length);

  const settledStageCount = topLevelStages.filter(
    (s) =>
      s.status === 'completed'
      || s.status === 'failed'
      || s.status === 'skipped',
  ).length;

  const progressCurrent = Math.max(
    root.display?.progress_current ?? 0,
    settledStageCount,
  );

  const rootParallelItems = Object.values(tree.containersById).filter(
    (node) =>
      node.scope === 'item'
      && node.workflow_path === root.workflow_path
      && node.workflow_depth === root.workflow_depth,
  );

  const rootParallelSettled =
    topLevelStages.length === 0
    && rootParallelItems.length > 0
    && rootParallelItems.every(
      (item) =>
        item.status === 'completed'
        || item.status === 'failed'
        || item.status === 'skipped',
    )
    && !Object.values(tree.containersById).some(
      (node) =>
        node.container_id !== rootId
        && node.workflow_path === root.workflow_path
        && node.workflow_depth === root.workflow_depth
        && (node.status === 'running'
          || node.status === 'retrying'
          || node.status === 'pending'),
    );

  const workflowVisuallyComplete =
    root.status === 'completed'
    || root.status === 'failed'
    || (progressTotal > 0 && progressCurrent >= progressTotal)
    || rootParallelSettled;

  if (!workflowVisuallyComplete) {
    return state;
  }

  let containersById = { ...tree.containersById };
  for (const [id, node] of Object.entries(containersById)) {
    if (node.status === 'running' || node.status === 'pending') {
      containersById[id] = { ...node, status: 'completed', is_open: false };
    }
  }

  const settledRoot = containersById[rootId] ?? root;
  if (settledRoot.status !== 'completed' && settledRoot.status !== 'failed') {
    containersById = {
      ...containersById,
      [rootId]: { ...settledRoot, status: 'completed', is_open: false },
    };
  }

  const loopTreesBySessionId = { ...state.loopTreesBySessionId };
  for (const [loopSessionId, loopTree] of Object.entries(loopTreesBySessionId)) {
    if (loopTree.status === 'running' || loopTree.status === 'connecting') {
      loopTreesBySessionId[loopSessionId] = finalizeWorkflowLoopTree(loopTree, 'done');
    }
  }

  const containerTree: WorkflowContainerTreeState = {
    ...tree,
    containersById,
    activeContainerIds: tree.activeContainerIds.filter(
      (id) => containersById[id]?.is_open,
    ),
  };

  const workflowProgress = syncWorkflowProgressFromContainerTree(
    containerTree,
    state.workflowProgress,
  );

  return {
    ...state,
    status: workflowProgress.status === 'error' ? 'error' : 'done',
    containerTree,
    workflowProgress,
    loopTreesBySessionId,
    activeLoopSessionId: null,
  };
}

/** 从空状态回放 workflow 事件序列。 */
export function reduceWorkflowEvents(
  events: LangchainAgentEvent[],
  options?: ReduceOptions,
): WorkflowSessionState {
  return events.reduce(
    (state, event, index) => reduceWorkflowSession(state, event, index, options),
    createEmptyWorkflowSessionState(),
  );
}
